import { Component, Input, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { SelectItem, MenuItem, LazyLoadEvent } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { CustomReportExportComponent } from 'src/app/components/extract-custom-report/custom-report-export.component';
import { AccessPermission } from 'src/app/models/dtos/access-controller/accessPermission';
import { VisitorFilter } from 'src/app/models/dtos/access-controller/visitorFilter';
import { PaginationFilter } from 'src/app/models/pagination-filter';
import { AccessGroupService } from 'src/app/services/access-controler/access-group.service';
import { VisitorService } from 'src/app/services/access-controler/visitor.service';
import { VisitorBlacklistService } from 'src/app/services/access-controler/visitor-blacklist.service';
import { CustomerService } from 'src/app/services/customer.service';
import { MessageUtil } from 'src/app/utils/message.util';

import { VisitorFormPermissionComponent } from '../service-provider-forms-container/visitor-form-permission/visitor-form-permission.component';
import { VisitorFormComponent } from '../service-provider-forms-container/visitor-form/visitor-form.component';
import { Visitor } from 'src/app/models/dtos/access-controller/visitor';
import { AccessGroupEnum } from 'src/app/models/dtos/access-controller/access-group-enum';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { VisitorFormHistoricComponent } from '../visitor-panel/visitor-form-historic/visitor-form-historic.component';
import { VisitorDTO } from 'src/app/models/dtos/access-controller/visitor-DTO';
import { BlacklistableVisitor } from 'src/app/models/dtos/access-controller/visitor-blacklist';
import { VisitorBlacklistDialogComponent } from '../visitor-blacklist-dialog/visitor-blacklist-dialog.component';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-visitor',
  templateUrl: './visitor.component.html',
  styleUrls: ['./visitor.component.scss'],
})
export class VisitorComponent implements OnInit {
  @Input()
  visitorType: AccessGroupEnum = null;

  visitorList: VisitorDTO[] = [];
  filteredVisitorList: VisitorDTO[] = [];
  allVisitorList: VisitorDTO[] = [];
  filter: VisitorFilter = new VisitorFilter();
  searchText: string = '';

  pageable: PaginationFilter = new PaginationFilter();
  registerToPrint: any[];
  customerList: SelectItem[] = [];
  rangeCreateDates: Date[];
  rangeCancelDates: Date[];
  accessGroupList: SelectItem[] = [{ label: 'Todos', value: null }];

  contentLoaded = false;
  totalRecords = 0;
  numberOfRows = 1000;
  menuItems: MenuItem[];
  selectedItem: Visitor;

  // CPF (só dígitos) -> id do bloqueio ativo, para badge e menu de retirar.
  blacklistByFederalId: Map<string, number> = new Map<string, number>();

  constructor(
    private accessGroupService: AccessGroupService,
    private customerService: CustomerService,
    private spinner: NgxSpinnerService,
    private messageUtil: MessageUtil,
    private visitorService: VisitorService,
    private visitorBlacklistService: VisitorBlacklistService,
    public dialog: DialogService
  ) {}

  ngOnInit() {
    // Inicializa o tamanho da página como 1000
    this.pageable.size = 1000;
    this.filter.pageSize = 1000;
    this.clear();
    this.loadMenuItems();
    BroadcastService.get('reloadTables').subscribe(() => {
      this.getVisitorsByFilter();
    });
  }

  loadMenuItems() {
    this.menuItems = this.buildMenuItems(false);
    this.accessGroupService.findAll().subscribe({
      next: (data) => {
        this.accessGroupList = data.map((group) => ({
          label: group.name,
          value: group.id,
        }));
        this.accessGroupList.unshift({ label: 'Todos', value: null });
        //TODO: FILTRAR PARA MOSTRAR APENAS OS GRUPOS DE VISITANTES
      },
    });
  }

  changeSelectedItem(visitor): void {
    this.selectedItem = visitor;
    this.menuItems = this.buildMenuItems(this.isBlacklisted(visitor));
  }

  buildMenuItems(blocked: boolean): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        iconClass: 'green-menu-icon',
        command: () => this.openVisitorForm(this.selectedItem),
      },
      {
        label: 'Permissões',
        icon: 'pi pi-key',
        iconClass: 'green-menu-icon',
        command: () => this.openVisitorPermission(this.selectedItem),
      },
      {
        label: 'Histórico',
        icon: 'pi pi-book',
        iconClass: 'red-menu-icon',
        command: () => this.openHistoricForm(this.selectedItem),
      },
    ];
    if (blocked) {
      items.push({
        label: 'Retirar da blacklist',
        icon: 'pi pi-unlock',
        iconClass: 'green-menu-icon',
        command: () => this.unblockVisitor(this.selectedItem),
      });
    } else {
      items.push({
        label: 'Adicionar à blacklist',
        icon: 'pi pi-ban',
        iconClass: 'red-menu-icon',
        command: () => this.blockVisitor(this.selectedItem),
      });
    }
    return items;
  }

  private normalizeCpf(value: string): string {
    return (value || '').replace(/\D/g, '');
  }

  isBlacklisted(visitor: BlacklistableVisitor): boolean {
    return this.blacklistByFederalId.has(this.normalizeCpf(visitor?.federalId));
  }

  loadBlacklist(): void {
    this.visitorBlacklistService.list().subscribe({
      next: (list) => {
        this.blacklistByFederalId = new Map<string, number>();
        (list || []).forEach((b) => {
          if (b.federalId) {
            this.blacklistByFederalId.set(this.normalizeCpf(b.federalId), b.id);
          }
        });
      },
      error: (error) => {
        // sem a lista, a tela mostraria todo mundo como "não bloqueado" em silêncio
        this.blacklistByFederalId = new Map<string, number>();
        this.messageUtil.generateMessage(
          'warning',
          'Blacklist',
          error?.error?.message ||
            'Não foi possível carregar a blacklist; os selos de bloqueio podem não aparecer.'
        );
      },
    });
  }

  openBlacklist(): void {
    this.dialog
      .open(VisitorBlacklistDialogComponent, {
        width: 'min(1000px, 95vw)',
        header: 'Blacklist de convidados',
      })
      .onClose.subscribe(() => this.loadBlacklist());
  }

  blockVisitor(visitor: BlacklistableVisitor): void {
    if (!visitor) {
      return;
    }
    Swal.fire({
      title: 'Adicionar à blacklist?',
      html:
        '<b>' + (visitor.name || '') + '</b><br>CPF ' + (visitor.federalId || '') +
        '<br><small>O convidado deixa de receber convites e, se tiver acesso liberado, ' +
        'é removido da catraca.</small>',
      icon: 'warning',
      input: 'text',
      inputPlaceholder: 'Motivo (opcional)',
      inputAttributes: { maxlength: '255' },
      showCancelButton: true,
      confirmButtonText: 'Bloquear',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      this.spinner.show();
      this.visitorBlacklistService
        .add({
          federalId: visitor.federalId,
          visitorId: visitor.id,
          name: visitor.name,
          reason: (result.value || '').toString().trim() || undefined,
        })
        .subscribe({
          next: () => {
            this.spinner.hide();
            this.messageUtil.generateMessage(
              'success',
              'Sucesso',
              'Convidado adicionado à blacklist.'
            );
            this.loadBlacklist();
          },
          error: (error) => {
            this.spinner.hide();
            this.handleBlacklistError(error);
          },
        });
    });
  }

  unblockVisitor(visitor: BlacklistableVisitor): void {
    if (!visitor) {
      return;
    }
    const id = this.blacklistByFederalId.get(
      this.normalizeCpf(visitor.federalId)
    );
    if (!id) {
      return;
    }
    Swal.fire({
      title: 'Retirar da blacklist?',
      html:
        '<b>' + (visitor.name || '') + '</b><br>CPF ' + (visitor.federalId || '') +
        '<br><small>O convidado volta a poder receber convites. O acesso revogado ao bloquear ' +
        'não volta sozinho: o titular precisa emitir um convite novo.</small>',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Retirar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      this.visitorBlacklistService.remove(id).subscribe({
        next: () => {
          this.messageUtil.generateMessage(
            'success',
            'Sucesso',
            'Convidado retirado da blacklist.'
          );
          this.loadBlacklist();
        },
        error: (error) => this.handleBlacklistError(error),
      });
    });
  }

  private handleBlacklistError(error: any): void {
    const exception = error?.error?.data?.exception;
    if (exception) {
      this.messageUtil.generateMessage(
        exception.type,
        exception.title,
        exception.message
      );
    } else {
      this.messageUtil.generateMessage(
        'error',
        'Erro',
        error?.error?.message || 'Não foi possível concluir a operação.'
      );
    }
  }

  openVisitorForm(selectvisitor?: Visitor): void {
    if (selectvisitor != null) {
      this.visitorService.getById(selectvisitor?.id).subscribe({
        next: (visitor) => {
          this.dialog
            .open(VisitorFormComponent, {
              width: 'min(1050px, 95vw)',
              header: 'Edição de Prestador de Serviço',
              data: {
                visitor: visitor,
                visitorType: this.visitorType,
              },
            })
            .onClose.subscribe(() => {
              BroadcastService.get('reloadTables').emit();
              this.getVisitorsByFilter();
            });
        },
        error: (error) => {
          console.log(error);
        },
      });
    }
  }

  getLastPermission(permissions: AccessPermission[]): AccessPermission {
    if (!permissions || permissions.length === 0) {
      return null;
    }

    let maxIdPermission: AccessPermission = permissions[0];

    for (const permission of permissions) {
      if (permission.id > maxIdPermission.id) {
        maxIdPermission = permission;
      }
    }

    return maxIdPermission;
  }

  openVisitorPermission(visitor?: Visitor): void {
    let accessGroupList = this.accessGroupList.filter(
      (group) => group.label !== 'Todos'
    );
    this.dialog
      .open(VisitorFormPermissionComponent, {
        // EBUG-238: a largura era fixa em 1400px e o .p-dialog nao tem max-width — nem no CSS do
        // dynamicdialog, nem no tema, nem no styles.scss. Como a mascara e display:flex com
        // justify-content:center, o dialogo transbordava CENTRALIZADO em viewport menor que a
        // largura pedida: metade ia para x negativo e ficava inalcançavel. O eixo vertical nao
        // precisa de nada — .p-dialog ja tem max-height:90% e .p-dialog-content ja e scroll
        // container com overflow-y:auto (dynamicdialog, ViewEncapsulation.None).
        width: 'min(1400px, 95vw)',
        header: 'Permissões de Acesso',
        data: {
          visitor: visitor,
          accessGroupList: accessGroupList,
          visitorType: this.visitorType,
        },
      })
      .onClose.subscribe(() => {
        BroadcastService.get('reloadTables').emit();
        this.getVisitorsByFilter();
      });
  }

  openHistoricForm(visitors?: Visitor): void {
    this.dialog
      .open(VisitorFormHistoricComponent, {
        width: '800px',
        height: '550px',
        header: 'Histórico de Utilização',
        data: {
          visitor: visitors,
        },
      })
      .onClose.subscribe(() => {});
  }

  async exportTable(): Promise<void> {
    await this.findDataExport();
    const data = { table: this.registerToPrint, type: 'VISITORS' };
    this.dialog.open(CustomReportExportComponent, {
      width: '70%',
      height: '90vh',
      dismissableMask: false,
      data,
      header: 'Prestadores de Serviço - Exportar Dados',
    });
  }

  async findDataExport(): Promise<void> {
    return new Promise(async (resolve) => {
      this.spinner.show();
      const filter = { ...this.filter };
      filter.pageSize = 1000000; // Mantém um valor alto para exportação
      filter.pageIndex = 0;

      this.visitorService.findByFilter(this.filter).subscribe(
        async (data: any) => {
          this.registerToPrint = data.content.map((visitor) => {
            return this.formatDataExport(visitor);
          });
          resolve();
        },
        async (error) => {
          this.spinner.hide();
          const exception = error.error.data.exception;
          this.messageUtil.generateMessage(
            exception.type,
            exception.title,
            exception.message
          );
          resolve();
        },
        () => this.spinner.hide()
      );
    });
  }

  formatDataExport(visitor): any {
    visitor.status = visitor.active ? 'Ativo' : 'Inativo';

    return {
      id: visitor.id,
      name: visitor.name,
      federalId: visitor.federalId,
      email: visitor.email,
      birthday: visitor.birthday,
      createdAt: visitor.createdAt,
      phone: visitor.phone,
      status: visitor.status,
      residentialZipCode: visitor.residentialAddress.zip,
      residentialCountry: visitor.residentialAddress.country,
      residentialDistrict: visitor.residentialAddress.district,
      residentialCity: visitor.residentialAddress.city,
      residentialState: visitor.residentialAddress.state,
      residentialAddress: visitor.residentialAddress.address,
      residentialNumber: visitor.residentialAddress.number,
      residentialComplement: visitor.residentialAddress.complement,
    };
  }

  getVisitorsByFilter(): void {
    this.loadBlacklist();
    this.filter.serviceProvider =
      this.visitorType === AccessGroupEnum.SERVICE_PROVIDER ? true : false;

    // Configurar o tamanho da página para 1000
    this.pageable.size = 1000;
    this.filter.pageSize = 1000;

    this.visitorService.findByFilter(this.filter).subscribe({
      next: (data) => {
        // Processar os dados para adicionar propriedades para ordenação
        this.allVisitorList = data.content.map(visitor => {
          const lastPermission = this.getLastPermission(visitor.accessPermissions);
          return {
            ...visitor,
            // Adicionar propriedade apenas para ordenação por data
            lastPermissionDate: lastPermission?.createDate || null
          };
        });
        this.visitorList = [...this.allVisitorList];
        this.filteredVisitorList = [...this.allVisitorList];
        this.totalRecords = data.totalElements;
      },
      error: (error) => {
        console.log(error);
        const exception = error.error.data.exception;
        this.messageUtil.generateMessage(
          exception.type,
          exception.title,
          exception.message
        );
      },
    });
  }

  getCustomers(): void {
    this.customerService.getAll('id,name').subscribe({
      next: (data) => {
        this.customerList = [{ label: 'Todos', value: null }].concat(
          data.map((c) => ({ label: c.name, value: c.id }))
        );
      },
      error: (error) => {
        const exception = error.error.data.exception;
        this.messageUtil.generateMessage(
          exception.type,
          exception.title,
          exception.message
        );
      },
    });
  }

  clear(): void {
    this.getCustomers();
    this.filter = new VisitorFilter();
    this.filter.serviceProvider =
      this.visitorType === AccessGroupEnum.SERVICE_PROVIDER ? true : false;
    this.filter.pageSize = 1000;
    this.pageable.size = 1000;
    this.rangeCreateDates = [];
    this.rangeCancelDates = [];
    this.getVisitorsByFilter();
  }

  onChangePage(event: LazyLoadEvent): void {
    // Não precisamos mais fazer requisições ao backend para paginação
    // A paginação será feita localmente com os dados já carregados
  }

  onChangeCadastro() {
    if (this.rangeCreateDates) {
      if (this.rangeCreateDates[0] != null) {
        this.filter.permissionStartDate = this.rangeCreateDates[0];
        if (this.rangeCreateDates[1] != null) {
          this.filter.permissionEndDate = this.rangeCreateDates[1];
        } else {
          this.filter.permissionEndDate = this.rangeCreateDates[0];
        }
      } else {
        this.filter.permissionStartDate = null;
        this.filter.permissionEndDate = null;
      }
    }
  }

  hasIncompleteVisitorData(visitor: VisitorDTO): boolean {
    if (!visitor.photoUrl) {
      return true;
    }
    if (
      !visitor.residentialAddressId == null &&
      this.visitorType === AccessGroupEnum.SERVICE_PROVIDER
    ) {
      return true;
    }
    return false;
  }

  filterTable(): void {
    if (!this.searchText || this.searchText.trim() === '') {
      this.visitorList = [...this.allVisitorList];
      this.totalRecords = this.allVisitorList.length;
      return;
    }
    
    const searchValue = this.searchText.toLowerCase().trim();
    this.visitorList = this.allVisitorList.filter(visitor => {
      // Obter o nome do responsável para filtragem
      const lastPermission = this.getLastPermission(visitor.accessPermissions);
      const responsibleName = lastPermission?.customerResponsibleName || '';
      
      return (
        (visitor.name && visitor.name.toLowerCase().includes(searchValue)) ||
        (visitor.federalId && visitor.federalId.toLowerCase().includes(searchValue)) ||
        (visitor.phone && visitor.phone.toLowerCase().includes(searchValue)) ||
        (responsibleName.toLowerCase().includes(searchValue))
      );
    });
    
    this.totalRecords = this.visitorList.length;
  }
  
  clearSearch(): void {
    this.searchText = '';
    this.filterTable();
  }
}
